/**
 * Componente AgGridTable para LocationOrders
 * 
 * Este es un wrapper del componente base AgGridTable que:
 * - Gestiona las columnas a través de useAgGridColumns
 * - Aplica opacidad durante refetch (NO durante infinite scroll)
 * - Mantiene la tabla siempre montada en el DOM
 * 
 * ⚠️ IMPORTANTE: NINGUNA columna es sortable según requerimiento del usuario
 */
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useRef, useEffect } from 'react';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { LocationOrdersModel } from '../../models/LocationOrders';

interface LocationOrdersAgGridTableProps {
  locationorders: LocationOrdersModel[];
  isLoading?: boolean; // 🎯 Para detectar cuando termina el loading
  isFetching?: boolean; // 🎯 Para control de opacidad
  className?: string;
  refetch?: () => void;
  // 🎯 Props para infinite scroll
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  // 🎯 Props para sorting personalizado (aunque no se usa porque NO hay sorting)
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (sorting: { field: string; direction: 'asc' | 'desc' } | null) => void;
  // 🎯 Props para columnas filtradas (patrón de Orders)
  columnDefs?: ColDef[];
}

export const LocationOrdersAgGridTable = React.memo(
  function LocationOrdersAgGridTable({
    locationorders,
    isLoading = false, // 🎯 Para detectar cuando termina el loading
    isFetching = false, // 🎯 Para control de opacidad
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    sorting,
    onSortingChange,
    columnDefs, // 🎯 Columnas filtradas desde Main
  }: LocationOrdersAgGridTableProps) {
    const agGridRef = useRef<AgGridTableRef>(null);

    // 🎯 Evitar crash: handler no-op para onGridReady
    const handleGridReady = useCallback(() => {}, []);

    // 🎯 Usar columnas filtradas desde Main (patrón de Orders)
    const columnDefsWithRenderers: ColDef[] = columnDefs || [];


    // 🎯 Usar las columnas reales del hook (patrón de Orders)

    return (
      <div
        className={`w-full ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <AgGridTable
          ref={agGridRef}
          data={locationorders}
          columnDefs={columnDefsWithRenderers} // 🎯 Patrón de Orders: usar columnas del hook
          height="calc(100vh - 150px)"
          enableSelection={false} // ⚠️ NO hay selección en LocationOrders
          onGridReady={handleGridReady}
          className="rounded-lg shadow-sm"
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          gridOptions={{}} // Pasamos un objeto vacío para evitar conflictos
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </div>
    );
  }
);
